import { useMemo } from 'react';

import { usePresentationApprovalRequirement } from '@/modules/tenant/TenantDetailsContext/hooks/usePresentationApprovalWorkflowEnabled';
import { ApprovalKind, PresentationApprovalRequirement } from '@/types/schema';

import {
  usePresentationApprovalKinds,
  usePresentationReviewRequirement,
} from '../ClientPresentationDesigner.context';

/**
 * Returns true when the presentation does not have the attestation and
 * approval records that it requires.
 */
export function isDraftPresentation(
  requirement: PresentationApprovalRequirement | null,
  approvalKinds: ApprovalKind[]
): boolean {
  const isAttested = approvalKinds.includes(ApprovalKind.PresentationAttested);
  const isApproved = approvalKinds.includes(ApprovalKind.PresentationApproved);

  switch (requirement) {
    case PresentationApprovalRequirement.AttestationOnly:
      return !isAttested;
    case PresentationApprovalRequirement.AttestationAndApproval:
      return !isAttested || !isApproved;
    default:
      // NOT_REQUIRED, no review applies to the current user, or the
      // presentation has not loaded.
      return false;
  }
}

/**
 * Returns true when the presentation slides must show the DRAFT watermark.
 * Collaborators never see the watermark, because the review workflow does not
 * apply to them. For other users, the result depends only on the review
 * requirement and the approvals of the presentation. A presentation created
 * before the tenant required a review step does not need that step.
 */
export function useIsDraftPresentation(): boolean {
  const isWorkflowEnabled = usePresentationApprovalRequirement() !== null;
  const presentationRequirement = usePresentationReviewRequirement();
  const requirement = isWorkflowEnabled ? presentationRequirement : null;
  const approvalKinds = usePresentationApprovalKinds();

  return useMemo(
    () => isDraftPresentation(requirement, approvalKinds),
    [requirement, approvalKinds]
  );
}
